import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import Sidebar from '@/components/Sidebar';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { toast } from 'sonner';
import { Edit, Trash2, Play } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Dashboard() {
  const [cars, setCars] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    fetchCars();
  }, []);

  const fetchCars = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/cars`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setCars(response.data);
    } catch (error) {
      toast.error('Failed to fetch cars');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (carId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/cars/${carId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Car deleted successfully');
      fetchCars();
    } catch (error) {
      toast.error('Failed to delete car');
    }
  };

  return (
    <div className="flex min-h-screen bg-zinc-950" data-testid="dashboard-page">
      <Sidebar />
      <main className="flex-1 p-8">
        <div className="max-w-7xl mx-auto">
          <div className="flex justify-between items-center mb-8">
            <div>
              <h1 className="text-4xl font-bold text-white mb-2" data-testid="dashboard-title">Car Inventory</h1>
              <p className="text-zinc-400" data-testid="dashboard-description">Manage your car collection</p>
            </div>
            <Button
              onClick={() => navigate('/add-car')}
              className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white px-6 py-6"
              data-testid="add-car-button"
            >
              + Add New Car
            </Button>
          </div>

          {loading ? (
            <div className="text-white text-center py-20" data-testid="loading-indicator">Loading cars...</div>
          ) : cars.length === 0 ? (
            <Card className="border-zinc-800 bg-zinc-900/50" data-testid="empty-state">
              <CardContent className="py-20 text-center">
                <p className="text-zinc-400 text-lg">No cars added yet. Start by adding your first car!</p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="cars-grid">
              {cars.map((car) => (
                <Card
                  key={car.id}
                  className="border-zinc-800 bg-zinc-900/50 overflow-hidden hover:border-zinc-700 transition-all duration-200 group"
                  data-testid={`car-card-${car.id}`}
                >
                  <div className="relative aspect-video bg-zinc-800">
                    {car.video_url ? (
                      <video
                        src={car.video_url}
                        className="w-full h-full object-cover"
                        controls={false}
                        data-testid={`car-video-${car.id}`}
                      >
                        Your browser does not support the video tag.
                      </video>
                    ) : (
                      <div className="flex items-center justify-center h-full text-zinc-600" data-testid={`car-no-video-${car.id}`}>
                        No video
                      </div>
                    )}
                    <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                      {car.video_url && (
                        <Play className="w-12 h-12 text-white" data-testid={`car-play-icon-${car.id}`} />
                      )}
                    </div>
                  </div>
                  <CardContent className="p-6">
                    <h3 className="text-xl font-bold text-white mb-2" data-testid={`car-title-${car.id}`}>{car.title}</h3>
                    {car.description && (
                      <p className="text-zinc-400 text-sm mb-4 line-clamp-2" data-testid={`car-description-${car.id}`}>{car.description}</p>
                    )}
                    {car.field_values && car.field_values.length > 0 && (
                      <div className="space-y-2 mb-4" data-testid={`car-fields-${car.id}`}>
                        {car.field_values.map((fv, idx) => (
                          <div key={idx} className="flex justify-between text-sm">
                            <span className="text-zinc-500">{fv.field_name}:</span>
                            <span className="text-zinc-300">{fv.value}</span>
                          </div>
                        ))}
                      </div>
                    )}
                    <div className="flex gap-2 pt-4 border-t border-zinc-800">
                      <Button
                        onClick={() => navigate(`/edit-car/${car.id}`)}
                        variant="outline"
                        className="flex-1 border-zinc-700 text-zinc-300 hover:bg-zinc-800 hover:text-white"
                        data-testid={`edit-car-button-${car.id}`}
                      >
                        <Edit className="w-4 h-4 mr-2" />
                        Edit
                      </Button>
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button
                            variant="outline"
                            className="flex-1 border-red-900 text-red-400 hover:bg-red-950 hover:text-red-300"
                            data-testid={`delete-car-button-${car.id}`}
                          >
                            <Trash2 className="w-4 h-4 mr-2" />
                            Delete
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent className="bg-zinc-900 border-zinc-800" data-testid={`delete-dialog-${car.id}`}>
                          <AlertDialogHeader>
                            <AlertDialogTitle className="text-white">Are you sure?</AlertDialogTitle>
                            <AlertDialogDescription className="text-zinc-400">
                              This will permanently delete the car and its video. This action cannot be undone.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel className="bg-zinc-800 text-white border-zinc-700 hover:bg-zinc-700" data-testid={`cancel-delete-${car.id}`}>
                              Cancel
                            </AlertDialogCancel>
                            <AlertDialogAction
                              onClick={() => handleDelete(car.id)}
                              className="bg-red-600 hover:bg-red-700 text-white"
                              data-testid={`confirm-delete-${car.id}`}
                            >
                              Delete
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
